import {Parameter} from 'platform/components';
import {NumberFormatter, useFormatNumber} from 'platform/locale';
import {match} from 'ts-pattern';

import {isNil, isNotNil, reject} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {useGetCurrentUserInfoQuery} from '@dms/api/customer';
import {GetRentalOrderApiResponse} from '@dms/api/rental';
import {GetVehicleApiResponseV2} from '@dms/api/shared';
import {GetRentalVehicleApiResponse} from '@dms/api/vehicle';
import i18n from '@dms/i18n';
import {useCodeList} from '@dms/shared';

import {EMPTY_PLACEHOLDER, Nullish, buildArray} from 'shared/web';

export function useRentalOrderHeaderParameters(
  vehicle: GetVehicleApiResponseV2 | Nullish,
  rentalOrder: GetRentalOrderApiResponse | Nullish,
  rentalVehicle: GetRentalVehicleApiResponse | Nullish
): Parameter[] {
  const formatNumber = useFormatNumber();

  const [carParkData] = useCodeList('carPark');
  const {data: userData} = useGetCurrentUserInfoQuery();

  const selectedCarParkName = carParkData?.find(
    (carPark) => carPark.codeId === rentalVehicle?.carParkId
  )?.name;

  if (isNil(rentalOrder) || isNil(rentalVehicle)) {
    return [
      `${i18n.t('general.labels.createdBy')}: ${reject(isNilOrEmpty, [userData?.firstName, userData?.lastName]).join(' ')}`,
    ];
  }

  return buildArray<Parameter>()
    .when(isNotNil(vehicle?.publicId), {
      title: vehicle?.publicId || EMPTY_PLACEHOLDER,
      tooltip: i18n.t('entity.vehicle.labels.publicId'),
    })
    .add({
      title: vehicle?.vin || EMPTY_PLACEHOLDER,
      tooltip: i18n.t('entity.vehicle.labels.vin'),
    })
    .add({
      title: vehicle?.registrationPlate || EMPTY_PLACEHOLDER,
      tooltip: i18n.t('entity.vehicle.labels.licensePlate'),
    })
    .add({
      title: selectedCarParkName || EMPTY_PLACEHOLDER,
      tooltip: i18n.t('entity.vehicle.labels.selectedCarPark'),
    })
    .add({
      title:
        formatMileage(formatNumber, rentalOrder?.rentVehicle?.actualMileage) || EMPTY_PLACEHOLDER,
      tooltip: i18n.t('entity.vehicle.labels.actualMileage'),
    })
    .add({
      title: formatFuelTank(rentalOrder?.rentVehicle?.fuelTank) || EMPTY_PLACEHOLDER,
      tooltip: i18n.t('entity.workshop.labels.fuelTank'),
    });
}

function formatFuelTank(value: string | Nullish): string | null {
  if (isNil(value)) {
    return null;
  }

  const roundedValue = Math.round(parseInt(value) / 0.25) * 0.25;

  return match(roundedValue)
    .with(0, () => 'E')
    .with(0.25, () => '1/4')
    .with(0.5, () => '1/2')
    .with(0.75, () => '3/4')
    .with(1, () => 'F')
    .otherwise(formatPercentage);
}

function formatPercentage(value: number | Nullish): string | null {
  if (isNil(value)) {
    return null;
  }

  return `${Math.round(value * 100)} %`;
}

const formatMileage = (formatNumber: NumberFormatter, mileage: number | Nullish) => {
  const formatedMileage = formatNumber(mileage);

  if (!formatedMileage) {
    return EMPTY_PLACEHOLDER;
  }

  return `${formatedMileage} ${i18n.t('general.metric.km')}`;
};
